import type { InitialGenerationNotificationPayload, StorageLike, SystemNotificationConstructor } from './const';
import {
  INITIAL_GENERATION_NOTIFICATION_TAG_PREFIX,
  SystemNotificationSkipReason,
} from './const';
import {
  getStorage,
  hasPromptedSystemNotificationPermission,
  markSystemNotificationPermissionPrompted,
} from './storage';

export * from './storage';

export type SystemNotificationPermissionState = NotificationPermission | 'unsupported';

export interface RequestSystemNotificationPermissionOptions {
  notificationApi?: SystemNotificationConstructor | null;
  storage?: StorageLike | null;
  ignorePrompted?: boolean;
}

export interface RequestSystemNotificationPermissionResult {
  requested: boolean;
  permission?: NotificationPermission;
  reason?: SystemNotificationSkipReason;
}

export interface InitialGenerationNotificationDecisionInput {
  payload?: Partial<InitialGenerationNotificationPayload> | null;
  armedProjectIds: readonly string[];
  runtimeShownKeys: ReadonlySet<string>;
  handledProjectIds: readonly string[];
  notificationPermission: SystemNotificationPermissionState;
  visibilityState?: DocumentVisibilityState | 'hidden';
}

export interface InitialGenerationNotificationDecision {
  shouldShow: boolean;
  reason?: SystemNotificationSkipReason;
  eventKey?: string;
  projectId?: string;
}

export interface ShowSystemNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  notificationApi?: SystemNotificationConstructor | null;
  onClick?: () => void;
}

export function getSystemNotificationApi(): SystemNotificationConstructor | null {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }
  return window.Notification as unknown as SystemNotificationConstructor;
}

export function getSystemNotificationPermission(
  notificationApi: SystemNotificationConstructor | null = getSystemNotificationApi(),
): SystemNotificationPermissionState {
  return notificationApi?.permission ?? 'unsupported';
}

export async function requestSystemNotificationPermissionOnce(
  userId: string,
  options: RequestSystemNotificationPermissionOptions = {},
): Promise<RequestSystemNotificationPermissionResult> {
  const notificationApi = options.notificationApi === undefined ? getSystemNotificationApi() : options.notificationApi;
  const storage = getStorage(options.storage);
  const permission = getSystemNotificationPermission(notificationApi);

  if (!notificationApi?.requestPermission || permission === 'unsupported') {
    return { requested: false, reason: SystemNotificationSkipReason.Unsupported };
  }
  if (permission !== 'default') {
    return { requested: false, permission, reason: SystemNotificationSkipReason.PermissionNotDefault };
  }
  if (!options.ignorePrompted && hasPromptedSystemNotificationPermission(userId, storage)) {
    return { requested: false, permission, reason: SystemNotificationSkipReason.AlreadyPrompted };
  }

  markSystemNotificationPermissionPrompted(userId, storage);
  try {
    return { requested: true, permission: await notificationApi.requestPermission() };
  } catch {
    return { requested: true, permission };
  }
}

export function buildInitialGenerationEventKey(projectId: string, messageId: string) {
  return `${projectId}:${messageId}`;
}

export function buildInitialGenerationNotificationTag(projectId: string) {
  return `${INITIAL_GENERATION_NOTIFICATION_TAG_PREFIX}-${projectId}`;
}

export function getInitialGenerationNotificationDecision(
  input: InitialGenerationNotificationDecisionInput,
): InitialGenerationNotificationDecision {
  const projectId = input.payload?.projectId;
  const messageId = input.payload?.messageId;

  if (!projectId || !messageId) {
    return { shouldShow: false, reason: SystemNotificationSkipReason.MissingPayload };
  }
  if (!input.armedProjectIds.includes(projectId)) {
    return { shouldShow: false, reason: SystemNotificationSkipReason.NotArmed, projectId };
  }

  const eventKey = buildInitialGenerationEventKey(projectId, messageId);
  if (input.runtimeShownKeys.has(eventKey)) {
    return { shouldShow: false, reason: SystemNotificationSkipReason.DuplicateRuntime, eventKey, projectId };
  }
  if (input.handledProjectIds.includes(projectId)) {
    return { shouldShow: false, reason: SystemNotificationSkipReason.DuplicateProject, eventKey, projectId };
  }
  if (input.notificationPermission !== 'granted') {
    return { shouldShow: false, reason: SystemNotificationSkipReason.PermissionNotGranted, eventKey, projectId };
  }
  if (input.visibilityState !== 'hidden') {
    return { shouldShow: false, reason: SystemNotificationSkipReason.Foreground, eventKey, projectId };
  }

  return { shouldShow: true, eventKey, projectId };
}

export function showSystemNotification(options: ShowSystemNotificationOptions): boolean {
  const NotificationApi = options.notificationApi === undefined ? getSystemNotificationApi() : options.notificationApi;
  if (!NotificationApi || getSystemNotificationPermission(NotificationApi) !== 'granted') {
    return false;
  }

  try {
    const notification = new NotificationApi(options.title, {
      body: options.body,
      icon: options.icon,
      tag: options.tag,
    });
    notification.onclick = () => {
      options.onClick?.();
      notification.close?.();
    };
    return true;
  } catch {
    return false;
  }
}
